<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

/* helpers */
import {
  capitalizeFirstLetter,
  isIosApp,
  isAndroidApp,
  isApp,
  showRewardInterstitial,  // Add this import
} from '@/helpers/utils'

/* composables */
import { useBackSwipeBlocker } from '@/composables/disableBackSwipeBlocker.js'

/* stores */
import { useAuthStore } from '@/stores/auth.store'
import { useCacheStore } from '@/stores/cache.store'
import { useProfileStore } from '@/stores/profile.store'
import { useTipStore } from '@/stores/tip.store'

/* components */
import LanguageStreakHeader from '@/components/LanguageStreakHeader.vue'
import DashboardPanel from '@/components/DashboardPanel.vue'
import DashboardPanelVocabSlide from '@/components/DashboardPanelVocabSlide.vue'
import DashboardPanelChatSlide from '@/components/DashboardPanelChatSlide.vue'
import DashboardPanelGenericSlide from '@/components/DashboardPanelGenericSlide.vue'

/* carousel */
import 'vue3-carousel/dist/carousel.css'
import { Carousel, Slide, Navigation } from 'vue3-carousel'

/* assets */
import chardCardPic from '@/assets/images/dashboard-chat.webp'
import flashCardsPic from '@/assets/images/flashcards.png'
import readingTogetherPic from '@/assets/images/dashboard-reading-together.webp'

/* router & i18n */
const router = useRouter()
const { t } = useI18n()

/* stores */
const authStore = useAuthStore()
const profileStore = useProfileStore()
const cacheStore = useCacheStore()
const tipStore = useTipStore()

/* state */
const isLoading = ref(true)
const profile = ref({})
const dashboard = ref({})
const showBillingWarning = ref(false)
const showBillingLock = ref(false)
const shouldShowEmailWarning = tipStore.checkTip('email_warning', true)
const topContent = ref(null)

/* swipe blocker */
const { blockSwipe, unblockSwipe } = useBackSwipeBlocker()

/* carousel config */
const carouselConfig = {
  itemsToShow: 'auto',
  wrapAround: true,
  mouseDrag: true,
  autoplay: 0,
  gap: 1,
  snapAlign: 'start',
}

/* lifecycle */
onMounted(async () => {
  await profileStore.fetchProfile(true)
  await loadData()
})

onBeforeUnmount(unblockSwipe)

/* methods */
async function loadData() {
  isLoading.value = true

  profile.value = profileStore.profile
  showBillingWarning.value = profile.value.billing_warning
  showBillingLock.value = profile.value.billing_locked

  if (!authStore.details.registration_complete) {
    router.push(isApp ? '/' : '/register')
    return
  }

  await cacheStore.process('dashboard', '/profile/dashboard/', data => {
    dashboard.value = data
  })

  isLoading.value = false
}

async function changeLanguage() {
  await loadData()
  await profileStore.fetchProfile(false)
}

// Add test function for reward interstitial
async function testRewardInterstitial() {
  try {
    await showRewardInterstitial()
  } catch (error) {
    console.error('Error showing reward interstitial:', error)
  }
}
</script>

<template>
  <nice-header />
  <loading-overlay v-model:active="isLoading" :can-cancel="true" is_full_page="false" loader="dots" :opacity="0.8" />
    <CModal :visible="showBillingLock" @close="() => { router.push('/billing')}"> 
		<CModalHeader>
			<CModalTitle>{{ t('billing.account-unavailable---billing-issue') }}</CModalTitle>
		</CModalHeader>
		<CModalBody>
      <p>{{ t('billing.we-are-sorry-but-your-account-is-currently') }}</p>
		</CModalBody>
	</CModal>
  <section class="my-0" ref="topContent">
    <div class="container">
      <language-streak-header @changeLanguage="changeLanguage"/>
      <div class="row">
        <div class="col-12">
          <div class="bg-white border-0 rounded border-light p-1 p-sm-2 p-md-5 w-100 fmxw-500">
            <div class="text-center text-md-center mb-3 mt-md-0 col-12">
              <h5 class=" fs-3 fw-bold" v-if="profile.first_name">{{ t("dashboard.welcome-back-firstname", { first_name: capitalizeFirstLetter(profile.first_name) }) }} 👋</h5>
              <p v-if="!isLoading" class="fs-5">
                {{ t('dashboard.lets-continue-learning--language-today-', { language: t(`language.name.${profileStore.profile.learning_language_id}`) }) }}
              </p>

            </div>
            <CAlert color="primary" v-if="isIosApp && profile.retired_ios">
              {{ t('profile.there-is-a-newer-version-of-lingo-ing-available') }}
              <br><br>
              <b><a href="https://apps.apple.com/ca/app/lingo-ing-ai-language-tutor/id6581480179">{{ t('profile.please-upgrade-here') }}</a></b>
            </CAlert>
            <CAlert color="primary" v-if="isAndroidApp && profile.retired_android">
              {{ t('profile.there-is-a-newer-version-of-lingo-ing-available') }}
              <br><br>
              <b><a href="https://play.google.com/store/apps/details?id=ing.lingo.androidapp">{{ t('profile.please-upgrade-here') }}</a></b>
            </CAlert>
            <CAlert color="warning" v-if="profile.pending_validation && !shouldShowEmailWarning">{{ t("dashboard.we-have-sent-a-validation-email-to-you", {email: profile.email}) }}  </CAlert>
            <CAlert color="warning" v-if="showBillingWarning && !showBillingLock">
            <b>{{ t("billing.billing-issue") }}</b><br />
              {{ t("billing.we-seem-to-be-having-an-issue-with") }}<br /><router-link to="/billing">{{ t('billing.billing-area') }}</router-link><br>
            </CAlert>
            <div class="d-flex gap-2"></div>
            <div class="row" >
              <div class="col-md-12 mt-1">
                <DashboardPanel to="/curriculum/progress" :image="flashCardsPic" :disableButton="true" :showCarousel="true">
                  <template v-slot:title><i class="bi bi-pencil me-2"></i> {{ t('dashboard.study') }}</template>
                  <template v-slot:description>{{ t('dashboard.learn-new-words-and-phrases-for-various-scenarios') }}</template>
                  <template v-slot:description_short>{{ t('dashboard.learn-new-words-and-phrases-for-various-scenarios') }}</template>
                  <template #call-to-action>
                    {{ t('dashboard.learn') }}
                  </template>
                  <template #slides>
                    <!--
                      <div v-disable-back-swipe>
                    -->
                    <div>
                      <Carousel
													@mousedown="blockSwipe"
													@mouseup="unblockSwipe"
													@touchstart="blockSwipe"
													@touchend="unblockSwipe"
													v-if="dashboard && dashboard.curriculum && dashboard.curriculum.topics"
                          v-bind="carouselConfig"
                          style="padding: 5px 10px"
                       >
                        <Slide v-for="(topic, index) in dashboard.curriculum.topics" :key="index">
                          <DashboardPanelVocabSlide :topic="topic" />
                        </Slide>
                        <Slide>
                          <DashboardPanelGenericSlide :title="t('dashboard.all-topics')" to="/curriculum/explore" />
                        </Slide>
                        <template #addons>
                          <Navigation class="slidescrollbtns" />
                        </template>
                      </Carousel>
                    </div>
                  </template>
                </DashboardPanel>
                <DashboardPanel to="/chat" :image="chardCardPic" :disableButton="true" :showCarousel="true"
                >
                  <template v-slot:title><i class="bi bi-chat me-2"></i> {{ t('dashboard.chat') }}</template>
                  <template v-slot:description>{{ t('dashboard.chat-with-an-ai--explore-role-playing') }}</template>
                  <template v-slot:description_short>{{ t('dashboard.boost-your-language-skills-with-our-exciting-chat').split('.')[0] }}</template>
                  <template #slides>
                    <!--<div v-disable-back-swipe> -->
                    <div >
                      <Carousel 
													@mousedown="blockSwipe"
													@mouseup="unblockSwipe"
													@touchstart="blockSwipe"
													@touchend="unblockSwipe"
                          v-if="dashboard && dashboard.chat && dashboard.chat" 
                          v-bind="carouselConfig"
                          style="padding: 5px 10px"
                      >
                        <Slide v-for="(chat_template, index) in dashboard.chat" :key="index" class="rounded-3">
                          <DashboardPanelChatSlide :chat_template="chat_template" />
                        </Slide>
                        <template #addons>
                          <Navigation class="slidescrollbtns" />
                        </template>
                      </Carousel>
                    </div>
                  </template>
                </DashboardPanel>
                <DashboardPanel to="/article/list/" :image="readingTogetherPic" :disableButton="true">
                  <template v-slot:title><i class="bi bi-book me-2"></i> {{ t('dashboard.reading-together') }}</template>
                  <template v-slot:description>{{ t('dashboard.reading-together-description') }}</template>
                  <template v-slot:description_short>{{ t('dashboard.reading-together-description').split('.')[0] }}</template>
                  <template v-slot:details>
                  </template>
                  <template #call-to-action>
                    {{ t('dashboard.read-articles') }}
                  </template>
                </DashboardPanel>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  <nice-footer />
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
h1,
h2 {
  font-weight: normal;
}

.carousel__slide {
  padding-right: 10px;
  padding-top: 10px;
  padding-bottom: 10px;
}
.carousel {
  --vc-nav-background: rgba(0, 0, 0, 0.8);
  --vc-nav-color: white;
  --vc-nav-color-hover: #e5e5e5;
  --vc-nav-border-radius: 50%;
  --vc-nav-width: 40px;
  --vc-nav-height: 40px;
}
.carousel-card {
  height: 115px;
  width: 200px;
  background-size: cover;
  overflow: hidden;
  transition: .5s ease;
}
.carousel-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1;
  transition: .5s ease;
}
.carousel-card:hover::before{
  background: rgba(0, 0, 0, 0);
  transition: .5s ease;
}
.carousel.card-body {
  height: 115px;
  width: 200px;
}

h5.card-title {
  font-size: 100%;
}
</style>


